import React from 'react';
import BookingCycle from '../../components/BookingCycle';
import {
    CircularProgress,
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { ExternalBookingLegType, ExternalBookingPaymentInfo, IExternalBooking } from '../../types/bookings';
import { showError, showSuccess } from '../../services/toaster';
import { buildExternalBookingReportUrl, openExternalBookingFitnessCertificate, openExternalBookingReport } from '../../services/externalBookings';
import { getStatusColor } from './constants';

interface ExternalBookingDetailsDialogProps {
    open: boolean;
    booking: IExternalBooking | null;
    paymentDetails: ExternalBookingPaymentInfo | null;
    reports?: string[];
    loading?: boolean;
    canManage?: boolean;
    onCancelBooking?: (reason: string) => Promise<void>;
    onRescheduleBooking?: (payload: { bookingDate: string; bookingTime: string; leg?: ExternalBookingLegType }) => Promise<void>;
    onUploadDocuments?: (type: 'report' | 'other' | 'fitness_certificate', files: File[], leg?: ExternalBookingLegType) => Promise<void>;
    onToggleSkipReport?: (skipReport: boolean) => Promise<void>;
    onToggleHealthCertificate?: (skipHealthCertificate: boolean) => Promise<void>;
    onToggleFitnessCertificateVerification?: (verified: boolean) => Promise<void>;
    onClose: () => void;
}

type VendorAction = 'reschedule' | 'upload_report' | 'upload_document' | 'upload_fitness_certificate' | 'cancel' | null;

const toLabelCase = (value?: string) => {
    if (!value) return '-';
    return value
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

const displayLegStatus = (leg?: { status?: string; normalizedStatus?: string }) =>
    leg?.normalizedStatus && leg.normalizedStatus !== 'unknown'
        ? leg.normalizedStatus
        : leg?.status;

const formatDate = (value?: string) => {
    if (!value) return '-';
    if (value.length === 8) {
        return `${value.substring(6, 8)}/${value.substring(4, 6)}/${value.substring(0, 4)}`;
    }
    return value;
};

const formatDateTime = (value?: string) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const formatCurrency = (amount?: number, currency: string = 'INR') => {
    if (typeof amount !== 'number') return '-';

    try {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency,
            maximumFractionDigits: 2,
        }).format(amount);
    } catch (error) {
        return `${currency} ${amount}`;
    }
};

const Field = ({ label, value }: { label: string; value?: React.ReactNode }) => (
    <Box>
        <Typography variant="caption" color="text.secondary">
            {label}
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5, wordBreak: 'break-word' }}>
            {value ?? '-'}
        </Typography>
    </Box>
);

const VendorInfoTile = ({
    label,
    value,
    icon,
    subtle = false,
}: {
    label: string;
    value?: React.ReactNode;
    icon?: React.ReactNode;
    subtle?: boolean;
}) => (
    <Box
        sx={{
            border: subtle ? '1px solid #eef2f7' : '1px solid #dfe7ef',
            borderRadius: 2,
            p: 2,
            minHeight: 86,
            backgroundColor: '#fff',
            boxShadow: subtle ? 'none' : '0 1px 2px rgba(15, 23, 42, 0.04)',
        }}
    >
        <Stack direction="row" spacing={1.2} alignItems="flex-start">
            {icon ? (
                <Box sx={{ color: '#09979a', display: 'flex', mt: 0.2 }}>
                    {icon}
                </Box>
            ) : null}
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary">
                    {label}
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 600, wordBreak: 'break-word' }}>
                    {value || '-'}
                </Typography>
            </Box>
        </Stack>
    </Box>
);

const VendorSection = ({
    title,
    action,
    children,
}: {
    title: string;
    action?: React.ReactNode;
    children: React.ReactNode;
}) => (
    <Box
        sx={{
            border: '1px solid #dfe7ef',
            borderRadius: 2,
            overflow: 'hidden',
            backgroundColor: '#fff',
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
        }}
    >
        <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
            sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid #eef2f7', backgroundColor: '#fbfcfd' }}
        >
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {title}
            </Typography>
            {action}
        </Stack>
        <Box sx={{ p: 2.5 }}>{children}</Box>
    </Box>
);

const hybridVisitTypes: Array<{ type: ExternalBookingLegType; title: string; key: 'centreVisit' | 'homeCollection'; index: number }> = [
    { type: 'CENTRE_VISIT', title: 'Centre Visit', key: 'centreVisit', index: 0 },
    { type: 'HOME_COLLECTION', title: 'Home Collection', key: 'homeCollection', index: 1 },
];

const toDateInputValue = (value?: string) =>
    value && /^\d{8}$/.test(value)
        ? `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`
        : '';

const HybridVisits = ({
    booking,
    onRescheduleLeg,
}: {
    booking: IExternalBooking;
    onRescheduleLeg?: (leg: ExternalBookingLegType) => void;
}) => (
    <Stack spacing={1.5}>
        {booking.externalGroupId && (
            <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                Group reference: {booking.externalGroupId}
            </Typography>
        )}
        <Grid container spacing={2}>
            {hybridVisitTypes.map(({ type, title, key, index }) => {
                const visit = booking.legs?.[key];
                const date = visit?.date || (index === 0 ? booking.bookingDate : undefined);
                const time = visit?.time || (index === 0 ? booking.bookingTime : undefined);
                const appointmentId = booking.externalBookingIds?.[index] ||
                    (index === 0 ? booking.externalBookingId : undefined);

                return (
                    <Grid item xs={12} md={6} key={type}>
                        <Box sx={{ borderTop: '1px solid #dfe7ef', pt: 1.5, height: '100%', minWidth: 0 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{title}</Typography>
                                {visit?.status && <Chip label={toLabelCase(displayLegStatus(visit))} size="small" variant="outlined" />}
                            </Stack>
                            <Grid container spacing={1.5}>
                                <Grid item xs={6}><Field label="Date" value={formatDate(date)} /></Grid>
                                <Grid item xs={6}><Field label="Time" value={time} /></Grid>
                                <Grid item xs={12}><Field label="Appointment ID" value={appointmentId} /></Grid>
                                {visit?.labId && <Grid item xs={12}><Field label="Lab ID" value={visit.labId} /></Grid>}
                                {(visit?.address || visit?.locality) && (
                                    <Grid item xs={12}>
                                        <Field label="Visit Address" value={[visit.address, visit.locality, visit.pincode].filter(Boolean).join(', ')} />
                                    </Grid>
                                )}
                            </Grid>
                            {onRescheduleLeg && (
                                <Button size="small" variant="outlined" sx={{ mt: 2 }} onClick={() => onRescheduleLeg(type)}>
                                    Reschedule {title}
                                </Button>
                            )}
                        </Box>
                    </Grid>
                );
            })}
        </Grid>
    </Stack>
);

export default function ExternalBookingDetailsDialog({
    open,
    booking,
    paymentDetails,
    reports = [],
    loading = false,
    canManage = false,
    onCancelBooking,
    onRescheduleBooking,
    onUploadDocuments,
    onToggleSkipReport,
    onToggleHealthCertificate,
    onToggleFitnessCertificateVerification,
    onClose,
}: ExternalBookingDetailsDialogProps) {
    const meta = booking?.meta || {};
    const displayStatus = booking?.packageType === 'HYBRID'
        ? booking.overallStatus || booking.status
        : booking?.status;
    const fitnessCertificates = booking?.documents?.filter((document) => document.type === 'fitness_certificate') || [];
    const payment = paymentDetails?.payment;
    const wallet = paymentDetails?.wallet;
    const refund = paymentDetails?.refund;
    const summary = paymentDetails?.summary;
    const currency = payment?.currency || 'INR';
    const [cancelReason, setCancelReason] = React.useState('');
    const [rescheduleDate, setRescheduleDate] = React.useState('');
    const [rescheduleTime, setRescheduleTime] = React.useState('');
    const [rescheduleLeg, setRescheduleLeg] = React.useState<ExternalBookingLegType | ''>('');
    const [uploadLeg, setUploadLeg] = React.useState<ExternalBookingLegType | ''>('');
    const [files, setFiles] = React.useState<File[]>([]);
    const [savingAction, setSavingAction] = React.useState(false);
    const [vendorAction, setVendorAction] = React.useState<VendorAction>(null);
    const [actionAnchorEl, setActionAnchorEl] = React.useState<HTMLElement | null>(null);

    React.useEffect(() => {
        setCancelReason('');
        setRescheduleDate('');
        setRescheduleTime('');
        setRescheduleLeg('');
        setUploadLeg('');
        setFiles([]);
        setVendorAction(null);
        setActionAnchorEl(null);
    }, [booking?._id]);

    const runAction = async (action: () => Promise<void>, successMessage: string) => {
        setSavingAction(true);
        try {
            await action();
            showSuccess(successMessage);
        } catch (error) {
            showError(error instanceof Error ? error.message : 'Action failed');
        } finally {
            setSavingAction(false);
        }
    };

    const handleCancel = () => {
        if (!onCancelBooking) return;
        const reason = cancelReason.trim();
        if (!reason) {
            showError('Cancellation reason is required');
            return;
        }
        void runAction(() => onCancelBooking(reason), 'Booking cancelled');
    };

    const handleReschedule = () => {
        if (!onRescheduleBooking) return;
        if (booking?.packageType === 'HYBRID' && !rescheduleLeg) {
            showError('Select the visit to reschedule');
            return;
        }
        if (!rescheduleDate || !rescheduleTime.trim()) {
            showError('Date and time are required');
            return;
        }
        void runAction(
            () => onRescheduleBooking({
                bookingDate: rescheduleDate.replace(/-/g, ''),
                bookingTime: rescheduleTime.trim(),
                ...(rescheduleLeg ? { leg: rescheduleLeg } : {}),
            }),
            booking?.packageType === 'HYBRID' ? 'Visit rescheduled' : 'Booking rescheduled'
        );
    };

    const selectRescheduleLeg = (leg: ExternalBookingLegType | '') => {
        setRescheduleLeg(leg);
        const visit = leg === 'CENTRE_VISIT' ? booking?.legs?.centreVisit : booking?.legs?.homeCollection;
        setRescheduleDate(toDateInputValue(visit?.date || (leg === 'CENTRE_VISIT' ? booking?.bookingDate : undefined)));
        setRescheduleTime(visit?.time || (leg === 'CENTRE_VISIT' ? booking?.bookingTime || '' : ''));
    };

    const selectVendorAction = (action: VendorAction, leg?: ExternalBookingLegType) => {
        setVendorAction(action);
        setActionAnchorEl(null);
        setFiles([]);
        setCancelReason('');
        setRescheduleDate('');
        setRescheduleTime('');
        setUploadLeg('');
        if (action === 'reschedule' && leg) {
            selectRescheduleLeg(leg);
        } else {
            setRescheduleLeg('');
        }
    };

    const handleUploadDocument = (type: 'report' | 'other' | 'fitness_certificate') => {
        if (!onUploadDocuments) return;
        if (type === 'report' && booking?.packageType === 'HYBRID' && !uploadLeg) {
            showError('Select the visit this report belongs to');
            return;
        }
        if (!files.length) {
            showError('Select at least one file');
            return;
        }
        void runAction(
            () => onUploadDocuments(type, files, uploadLeg || undefined),
            type === 'report' ? 'Report uploaded' : type === 'fitness_certificate' ? 'Fitness certificate uploaded' : 'Document uploaded'
        );
    };

    const handleAdminFitnessCertificateUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = Array.from(event.target.files || []);
        event.target.value = '';
        if (!selectedFiles.length || !onUploadDocuments) return;
        void runAction(
            () => onUploadDocuments('fitness_certificate', selectedFiles),
            'Fitness certificate uploaded and verified'
        );
    };

    if (canManage) {
        const packageTestNames = [
            ...(booking?.packageNames || []),
            ...(booking?.testNames || []),
        ].join(', ') || (Array.isArray(meta.packageIds) ? meta.packageIds.join(', ') : '');
        const otherDocuments = booking?.documents?.filter((document) => document.type === 'other') || [];
        const canUpdateBooking = Boolean(booking && !['cancelled', 'abandoned'].includes(booking.status || ''));
        const closeActionButton = (
            <IconButton
                size="small"
                onClick={() => selectVendorAction(null)}
                aria-label="Close action panel"
                sx={{ color: 'text.secondary' }}
            >
                <CloseIcon fontSize="small" />
            </IconButton>
        );
        const vendorActionPanel = (
            <>
                {!canUpdateBooking && (
                    <Box sx={{ p: 1.5, borderRadius: 1.5, backgroundColor: '#fff4f4', border: '1px solid #ffd6d6' }}>
                        <Typography variant="body2" color="error">
                            This booking is {booking?.status === 'abandoned' ? 'abandoned because payment was not completed' : 'cancelled'}. Actions are disabled.
                        </Typography>
                    </Box>
                )}

                {vendorAction === 'reschedule' && booking && (
                    <VendorSection title="Reschedule Booking" action={closeActionButton}>
                        <Grid container spacing={2}>
                            {booking.packageType === 'HYBRID' && (
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        select
                                        required
                                        size="small"
                                        label="Visit to reschedule"
                                        value={rescheduleLeg}
                                        onChange={(event) => selectRescheduleLeg(event.target.value as ExternalBookingLegType | '')}
                                        disabled={!canUpdateBooking}
                                        helperText="Only the selected visit will move; the other visit stays unchanged."
                                    >
                                        <MenuItem value="">Select visit</MenuItem>
                                        <MenuItem value="CENTRE_VISIT">Centre Visit</MenuItem>
                                        <MenuItem value="HOME_COLLECTION">Home Collection</MenuItem>
                                    </TextField>
                                </Grid>
                            )}
                            <Grid item xs={12} md={booking.packageType === 'HYBRID' ? 4 : 5}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="New Date"
                                    type="date"
                                    value={rescheduleDate}
                                    onChange={(event) => setRescheduleDate(event.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    disabled={!canUpdateBooking}
                                />
                            </Grid>
                            <Grid item xs={12} md={booking.packageType === 'HYBRID' ? 4 : 5}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="New Time"
                                    value={rescheduleTime}
                                    onChange={(event) => setRescheduleTime(event.target.value)}
                                    disabled={!canUpdateBooking}
                                />
                            </Grid>
                            <Grid item xs={12} md={booking.packageType === 'HYBRID' ? 12 : 2}>
                                <Button
                                    variant="contained"
                                    onClick={handleReschedule}
                                    disabled={savingAction || !canUpdateBooking || (booking.packageType === 'HYBRID' && !rescheduleLeg)}
                                    fullWidth
                                    sx={{ height: 40 }}
                                    startIcon={<CalendarTodayOutlinedIcon />}
                                >
                                    Save
                                </Button>
                            </Grid>
                        </Grid>
                    </VendorSection>
                )}

                {(vendorAction === 'upload_report' || vendorAction === 'upload_document' || vendorAction === 'upload_fitness_certificate') && (
                    <VendorSection
                        title={vendorAction === 'upload_report' ? 'Upload Report' : vendorAction === 'upload_fitness_certificate' ? 'Upload Fitness Certificate' : 'Upload Document'}
                        action={closeActionButton}
                    >
                        <Grid container spacing={2} alignItems="flex-start">
                            {vendorAction === 'upload_report' && booking?.packageType === 'HYBRID' && (
                                <Grid item xs={12}>
                                    <TextField
                                        select
                                        fullWidth
                                        size="small"
                                        label="Report for"
                                        value={uploadLeg}
                                        onChange={(event) => setUploadLeg(event.target.value as ExternalBookingLegType)}
                                        disabled={!canUpdateBooking}
                                        required
                                    >
                                        <MenuItem value="CENTRE_VISIT">Centre visit</MenuItem>
                                        <MenuItem value="HOME_COLLECTION">Home collection</MenuItem>
                                    </TextField>
                                </Grid>
                            )}
                            <Grid item xs={12} md={9}>
                                <Button
                                    variant="outlined"
                                    component="label"
                                    fullWidth
                                    disabled={!canUpdateBooking}
                                    sx={{ height: 44 }}
                                    startIcon={<FileUploadOutlinedIcon />}
                                >
                                    Select Files
                                    <input
                                        hidden
                                        multiple
                                        type="file"
                                        onChange={(event) => setFiles(Array.from(event.target.files || []))}
                                    />
                                </Button>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                                    {files.length ? files.map((file) => file.name).join(', ') : 'No files selected'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Button
                                    variant="contained"
                                    onClick={() => handleUploadDocument(vendorAction === 'upload_report' ? 'report' : vendorAction === 'upload_fitness_certificate' ? 'fitness_certificate' : 'other')}
                                    disabled={savingAction || !canUpdateBooking || (vendorAction === 'upload_report' && booking?.packageType === 'HYBRID' && !uploadLeg)}
                                    fullWidth
                                    sx={{ height: 44 }}
                                >
                                    Upload
                                </Button>
                            </Grid>
                        </Grid>
                    </VendorSection>
                )}

                {vendorAction === 'cancel' && (
                    <VendorSection title="Cancel Booking" action={closeActionButton}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={9}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Cancellation Reason"
                                    value={cancelReason}
                                    onChange={(event) => setCancelReason(event.target.value)}
                                    disabled={!canUpdateBooking}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Button
                                    color="error"
                                    variant="outlined"
                                    onClick={handleCancel}
                                    disabled={savingAction || !canUpdateBooking}
                                    fullWidth
                                    sx={{ height: 40 }}
                                    startIcon={<CancelOutlinedIcon />}
                                >
                                    Cancel Booking
                                </Button>
                            </Grid>
                        </Grid>
                    </VendorSection>
                )}
            </>
        );

        return (
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        maxHeight: '88vh',
                    },
                }}
            >
                <DialogTitle sx={{ px: 3, py: 2.5, borderBottom: '1px solid #dfe7ef', backgroundColor: '#f8fbfb' }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1f2933' }}>
                                Booking Details
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {booking?.externalBookingId ? `Booking reference ${booking.externalBookingId}` : 'Booking reference pending'}
                            </Typography>
                        </Box>
                        {booking ? (
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', sm: 'center' }}>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    <Chip label={toLabelCase(booking.providerType)} color="primary" sx={{ fontWeight: 700 }} />
                                    <Chip
                                        label={toLabelCase(displayStatus)}
                                        color={getStatusColor(displayStatus)}
                                        variant="outlined"
                                        sx={{ fontWeight: 700, backgroundColor: '#fff' }}
                                    />
                                </Stack>
                                {onToggleSkipReport ? (
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                        <Checkbox
                                            size="small"
                                            checked={Boolean(booking.skipReport)}
                                            onChange={(event) => {
                                                void runAction(
                                                    () => onToggleSkipReport(event.target.checked),
                                                    'Report setting updated'
                                                );
                                            }}
                                            disabled={savingAction}
                                        />
                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>Skip report</Typography>
                                    </Stack>
                                ) : null}
                                <Button
                                    variant="contained"
                                    onClick={(event) => setActionAnchorEl(event.currentTarget)}
                                    disabled={!canUpdateBooking}
                                    endIcon={<KeyboardArrowDownIcon />}
                                    sx={{ minWidth: 132 }}
                                >
                                    Actions
                                </Button>
                                <Menu
                                    anchorEl={actionAnchorEl}
                                    open={Boolean(actionAnchorEl)}
                                    onClose={() => setActionAnchorEl(null)}
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                >
                                    <MenuItem onClick={() => selectVendorAction('reschedule')}>
                                        Reschedule
                                    </MenuItem>
                                    <MenuItem onClick={() => selectVendorAction('upload_report')}>
                                        Upload Report
                                    </MenuItem>
                                    <MenuItem onClick={() => selectVendorAction('upload_document')}>
                                        Upload Document
                                    </MenuItem>
                                    <MenuItem onClick={() => selectVendorAction('upload_fitness_certificate')}>
                                        Upload Fitness Certificate
                                    </MenuItem>
                                    <MenuItem onClick={() => selectVendorAction('cancel')} sx={{ color: 'error.main' }}>
                                        Cancel Booking
                                    </MenuItem>
                                </Menu>
                            </Stack>
                        ) : null}
                    </Stack>
                </DialogTitle>

                <DialogContent sx={{ p: 0 }}>
                    {!booking ? null : loading ? (
                        <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ py: 8 }}>
                            <CircularProgress size={28} />
                            <Typography variant="body2" color="text.secondary">
                                Loading booking details...
                            </Typography>
                        </Stack>
                    ) : (
                        <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: '#f4f7f9' }}>
                            <Stack spacing={2.5}>
                                {vendorActionPanel}

                                <VendorSection title="Customer">
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={4}>
                                            <VendorInfoTile label="Name" value={booking.patientName} icon={<PersonOutlineOutlinedIcon fontSize="small" />} />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <VendorInfoTile label="Phone" value={booking.patientPhone} />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <VendorInfoTile label="Email" value={booking.patientEmail} />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <VendorInfoTile label="Address" value={booking.address || meta.address || meta.addressLine} icon={<PlaceOutlinedIcon fontSize="small" />} />
                                        </Grid>
                                    </Grid>
                                </VendorSection>

                                <VendorSection title="Appointment">
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={3}>
                                            <VendorInfoTile label={booking.packageType === 'HYBRID' ? 'Centre Visit Date' : 'Date'} value={formatDate(booking.bookingDate)} icon={<CalendarTodayOutlinedIcon fontSize="small" />} />
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <VendorInfoTile label={booking.packageType === 'HYBRID' ? 'Centre Visit Time' : 'Time'} value={booking.bookingTime} />
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <VendorInfoTile label="Type" value={toLabelCase(booking.appointmentType)} />
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <VendorInfoTile
                                                label="Packages / Tests"
                                                value={packageTestNames}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <VendorInfoTile label="Lab / Doctor" value={booking.labName || booking.doctorName || meta.labName || meta.doctorName} />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <VendorInfoTile label="Visit Type" value={toLabelCase(booking.packageType || meta.packageType)} subtle />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <VendorInfoTile label="Slot Reference" value={booking.externalSlotRef} subtle />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <VendorInfoTile label="Created" value={formatDateTime(booking.createdAt)} subtle />
                                        </Grid>
                                    </Grid>
                                </VendorSection>

                                {booking.packageType === 'HYBRID' && (
                                    <VendorSection title="Hybrid Visits">
                                        <HybridVisits
                                            booking={booking}
                                            onRescheduleLeg={canUpdateBooking && onRescheduleBooking
                                                ? (leg) => selectVendorAction('reschedule', leg)
                                                : undefined}
                                        />
                                    </VendorSection>
                                )}

                                <Grid container spacing={2.5}>
                                    <Grid item xs={12} md={6}>
                                        <VendorSection
                                            title="Reports"
                                            action={<Chip label={`${reports.length} file${reports.length === 1 ? '' : 's'}`} size="small" variant="outlined" />}
                                        >
                                            {reports.length > 0 ? (
                                                <Stack spacing={1}>
                                                    {reports.map((reportUrl, index) => (
                                                        <Button
                                                            key={`${reportUrl}-${index}`}
                                                            variant="outlined"
                                                            onClick={() => void runAction(
                                                                () => openExternalBookingReport(reportUrl),
                                                                'Report opened'
                                                            )}
                                                            startIcon={<DescriptionOutlinedIcon />}
                                                            sx={{ justifyContent: 'flex-start' }}
                                                        >
                                                            Download Report {index + 1}
                                                        </Button>
                                                    ))}
                                                </Stack>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    {booking.skipReport ? 'Reports are disabled for this booking.' : 'No report uploaded yet.'}
                                                </Typography>
                                            )}
                                        </VendorSection>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <VendorSection
                                            title="Other Documents"
                                            action={<Chip label={`${otherDocuments.length} file${otherDocuments.length === 1 ? '' : 's'}`} size="small" variant="outlined" />}
                                        >
                                            {otherDocuments.length ? (
                                                <Stack spacing={1}>
                                                    {otherDocuments.map((document, index) => (
                                                        <Button
                                                            key={`${document.url}-${index}`}
                                                            variant="outlined"
                                                            component="a"
                                                            href={buildExternalBookingReportUrl(document.url)}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            startIcon={<DescriptionOutlinedIcon />}
                                                            sx={{ justifyContent: 'flex-start' }}
                                                        >
                                                            {document.originalFileName || document.fileName || `Document ${index + 1}`}
                                                        </Button>
                                                    ))}
                                                </Stack>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    No other documents uploaded.
                                                </Typography>
                                            )}
                                        </VendorSection>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <VendorSection
                                            title="Fitness Certificate"
                                            action={<Chip label={booking.isFitnessCertificateVerified ? 'Verified' : 'Pending verification'} size="small" color={booking.isFitnessCertificateVerified ? 'success' : 'default'} />}
                                        >
                                            {fitnessCertificates.length ? (
                                                <Stack spacing={1}>
                                                    {fitnessCertificates.map((document, index) => (
                                                        <Button
                                                            key={`${document.url}-${index}`}
                                                            variant="outlined"
                                                            onClick={() => booking?._id && void runAction(
                                                                () => openExternalBookingFitnessCertificate(booking._id, document.fileName || ''),
                                                                'Fitness certificate opened'
                                                            )}
                                                            startIcon={<DescriptionOutlinedIcon />}
                                                        >
                                                            {document.originalFileName || document.fileName || `Fitness Certificate ${index + 1}`}
                                                        </Button>
                                                    ))}
                                                </Stack>
                                            ) : <Typography variant="body2" color="text.secondary">No fitness certificate uploaded yet.</Typography>}
                                        </VendorSection>
                                    </Grid>
                                </Grid>

                            </Stack>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e5e7eb' }}>
                    <Button onClick={onClose}>Close</Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>External Booking Details</DialogTitle>
            <DialogContent dividers>
                {!booking ? null : loading ? (
                    <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ py: 6 }}>
                        <CircularProgress size={28} />
                        <Typography variant="body2" color="text.secondary">
                            Loading payment details...
                        </Typography>
                    </Stack>
                ) : (
                    <Stack spacing={3}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ flex: 1 }}>
                                <Chip label={toLabelCase(booking.providerType)} size="small" color="primary" />
                                <Chip label={toLabelCase(displayStatus)} size="small" color={getStatusColor(displayStatus)} variant="outlined" />
                                <Chip label={toLabelCase(booking.paymentStatus)} size="small" variant="outlined" />
                            </Stack>
                            {onToggleSkipReport ? (
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                    <Checkbox
                                        size="small"
                                        checked={Boolean(booking.skipReport)}
                                        onChange={(event) => {
                                            void runAction(
                                                () => onToggleSkipReport(event.target.checked),
                                                'Report setting updated'
                                            );
                                        }}
                                        disabled={savingAction}
                                    />
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>Skip report</Typography>
                                </Stack>
                            ) : null}
                            {onToggleHealthCertificate ? (
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                    <Checkbox
                                        size="small"
                                        checked={booking.skipHealthCertificate === false}
                                        onChange={(event) => {
                                            void runAction(
                                                () => onToggleHealthCertificate(!event.target.checked),
                                                'Health certificate setting updated'
                                            );
                                        }}
                                        disabled={savingAction}
                                    />
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>Enable health certificate</Typography>
                                </Stack>
                            ) : null}
                            {onToggleFitnessCertificateVerification ? (
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                    <Checkbox
                                        size="small"
                                        checked={Boolean(booking.isFitnessCertificateVerified)}
                                        onChange={(event) => {
                                            void runAction(
                                                () => onToggleFitnessCertificateVerification(event.target.checked),
                                                'Fitness certificate verification updated'
                                            );
                                        }}
                                        disabled={savingAction || (fitnessCertificates.length === 0 && !booking.isFitnessCertificateVerified)}
                                    />
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>Fitness certificate verified</Typography>
                                </Stack>
                            ) : null}
                        </Stack>

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Field label="Patient Name" value={booking.patientName} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Field label="User ID" value={booking.userId} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Field label="Phone" value={booking.patientPhone} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Field label="Email" value={booking.patientEmail} />
                            </Grid>
                            <Grid item xs={12}>
                                <Field label="Address" value={booking.address || meta.address || meta.addressLine} />
                            </Grid>
                        </Grid>

                        <Divider />

                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Booking
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <Field label={booking.packageType === 'HYBRID' ? 'Centre Visit Date' : 'Booking Date'} value={formatDate(booking.bookingDate)} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Field label={booking.packageType === 'HYBRID' ? 'Centre Visit Time' : 'Booking Time'} value={booking.bookingTime} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Field label="Appointment Type" value={toLabelCase(booking.appointmentType)} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Field label="Integration" value={toLabelCase(booking.integration)} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Field label="External Provider ID" value={booking.externalProviderId} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Field label="External Booking ID" value={booking.externalBookingId} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Field label="External Slot Ref" value={booking.externalSlotRef} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Field label="Internal Booking ID" value={booking._id} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Field label="Payment Transaction ID" value={booking.paymentTransactionId} />
                            </Grid>
                        </Grid>

                        {booking.packageType === 'HYBRID' && (
                            <>
                                <Divider />
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Hybrid Visits</Typography>
                                <HybridVisits booking={booking} />
                            </>
                        )}

                        <Divider />

                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Payment
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <Field label="Price" value={typeof booking.price === 'number' ? `₹${booking.price}` : '-'} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Field label="Payment Status" value={toLabelCase(booking.paymentStatus)} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Field label={booking.packageType === 'HYBRID' ? 'Overall Status' : 'Booking Status'} value={toLabelCase(displayStatus)} />
                            </Grid>
                            {booking.packageType === 'HYBRID' && (
                                <Grid item xs={12} md={4}>
                                    <Field label="Group Status" value={toLabelCase(booking.status)} />
                                </Grid>
                            )}
                            <Grid item xs={12} md={6}>
                                <Field label={booking.status === 'abandoned' ? 'Abandonment Reason' : 'Cancellation Reason'} value={meta.cancellationReason} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Field
                                    label="Payment Timeout / Confirmation"
                                    value={meta.paymentTimeoutCancelledAt || meta.paymentConfirmationTimedOutAt}
                                />
                            </Grid>
                        </Grid>

                        {(payment || wallet || refund || summary) && (
                            <>
                                <Divider />

                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    Payment Breakdown
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={4}>
                                        <Field label="Payment Gateway" value={toLabelCase(payment?.paymentGateway)} />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Field label="Coupon Code" value={summary?.couponCode || payment?.couponCode} />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Field label="Currency" value={payment?.currency || 'INR'} />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Field label="Base Amount" value={formatCurrency(payment?.baseAmount, currency)} />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Field label="Discount Amount" value={formatCurrency(payment?.discountAmount, currency)} />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Field label="Final Amount" value={formatCurrency(payment?.finalAmount, currency)} />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Field
                                            label="Charged To Razorpay"
                                            value={formatCurrency(summary?.amountChargedToRazorpay, currency)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Field
                                            label="Used From Wallet"
                                            value={formatCurrency(summary?.amountUsedFromWallet ?? wallet?.amountUsed, currency)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Field
                                            label="Refunded Amount"
                                            value={formatCurrency(summary?.amountRefunded ?? refund?.amountRefunded, currency)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Field
                                            label="Actual Amount Paid"
                                            value={formatCurrency(summary?.actualAmountPaid, currency)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Field label="Paid At" value={formatDateTime(summary?.paidAt || payment?.paidAt)} />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Field label="Transaction ID" value={payment?.paymentTransactionId || booking.paymentTransactionId} />
                                    </Grid>
                                </Grid>
                            </>
                        )}

                        <>
                            <Divider />

                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                Reports
                            </Typography>
                            {reports.length > 0 ? (
                                <Stack spacing={1}>
                                    {reports.map((reportUrl, index) => (
                                        <Button
                                            key={`${reportUrl}-${index}`}
                                            variant="outlined"
                                            onClick={() => void runAction(
                                                () => openExternalBookingReport(reportUrl),
                                                'Report opened'
                                            )}
                                            sx={{ alignSelf: 'flex-start' }}
                                        >
                                            Download Report {index + 1}
                                        </Button>
                                    ))}
                                </Stack>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    {booking.skipReport ? 'Reports are disabled for this booking.' : 'No reports available for this booking.'}
                                </Typography>
                            )}
                        </>

                        <Divider />

                        {(fitnessCertificates.length > 0 || onToggleFitnessCertificateVerification) && (
                            <>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Fitness Certificate</Typography>
                                    {onToggleFitnessCertificateVerification && onUploadDocuments ? (
                                        <Button variant="outlined" size="small" component="label" disabled={savingAction}>
                                            Upload Fitness Certificate
                                            <input
                                                hidden
                                                multiple
                                                type="file"
                                                accept=".pdf,.png,.jpg,.jpeg,.webp"
                                                onChange={handleAdminFitnessCertificateUpload}
                                            />
                                        </Button>
                                    ) : null}
                                </Stack>
                                {fitnessCertificates.length ? (
                                    <Stack spacing={1}>
                                        {fitnessCertificates.map((document, index) => (
                                            <Button
                                                key={`${document.url}-${index}`}
                                                variant="outlined"
                                                onClick={() => void runAction(
                                                    () => openExternalBookingFitnessCertificate(booking._id, document.fileName || ''),
                                                    'Fitness certificate opened'
                                                )}
                                                sx={{ alignSelf: 'flex-start' }}
                                            >
                                                View Fitness Certificate {index + 1}
                                            </Button>
                                        ))}
                                    </Stack>
                                ) : <Typography variant="body2" color="text.secondary">No fitness certificate uploaded.</Typography>}
                                <Divider />
                            </>
                        )}

                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Documents</Typography>
                        {booking.documents?.some((document) => document.type !== 'fitness_certificate') ? (
                            <Stack spacing={1}>
                                {booking.documents.filter((document) => document.type !== 'fitness_certificate').map((document, index) => (
                                    <Button
                                        key={`${document.url}-${index}`}
                                        variant="text"
                                        onClick={() => void runAction(
                                            () => openExternalBookingReport(document.url),
                                            'Document opened'
                                        )}
                                        sx={{ alignSelf: 'flex-start' }}
                                    >
                                        {toLabelCase(document.type)} - {document.originalFileName || document.fileName || `Document ${index + 1}`}
                                    </Button>
                                ))}
                            </Stack>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No documents uploaded.
                            </Typography>
                        )}

                        <Divider />

                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Additional Details
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <Field label="Primary Concern" value={meta.primaryConcern} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Field label="Consultation Mode" value={toLabelCase(meta.consultationMode)} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Field label="Lab Name" value={meta.labName} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Field label="Patient Gender" value={toLabelCase(meta.patientGender)} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Field label="Patient Age" value={meta.patientAge} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Field label="Date of Birth" value={formatDate(meta.patientDateOfBirth)} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Field label="City" value={meta.city} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Field label="State" value={meta.state} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Field label="Pincode" value={meta.pincode} />
                            </Grid>
                            <Grid item xs={12}>
                                <Field
                                    label="Packages"
                                    value={Array.isArray(meta.packageIds) && meta.packageIds.length ? meta.packageIds.join(', ') : '-'}
                                />
                            </Grid>
                        </Grid>

                        <Divider />

                        <BookingCycle lifecycle={booking.lifecycle} createdAt={booking.createdAt} external />
                        {booking.packageType === 'HYBRID' && <>
                            <BookingCycle title="Centre visit cycle" lifecycle={booking.visitLifecycle?.centreVisit} external />
                            <BookingCycle title="Home collection cycle" lifecycle={booking.visitLifecycle?.homeCollection} external />
                        </>}

                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Audit</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Field label="Created At" value={formatDateTime(booking.createdAt)} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Field label="Updated At" value={formatDateTime(booking.updatedAt)} />
                            </Grid>
                        </Grid>
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
